<template>
  <div :class="attrs.class">
    <FormLabel
      v-if="slots.label"
      :tooltip="tooltip"
      :is-required="isRequired"
      :id="id"
      class="tw-mb-2 dark:tw-text-white"
    >
      <slot name="label" />
    </FormLabel>
    <div class="tw-flex tw-space-x-2">
      <textarea
        v-if="as === 'textarea'"
        v-bind="sharedAttributes"
        @input="handleInputChange"
      />
      <input v-else v-bind="sharedAttributes" @input="handleInputChange" />
      <slot name="input-append" />
    </div>
    <div
      v-if="hasErrors || isCharacterCounterShown || isCaptionShown"
      class="tw-mt-2 tw-space-y-1"
    >
      <FormErrors v-if="hasErrors" :id="id" :errors="errors" />
      <FormCaption v-if="isCharacterCounterShown">
        {{ modelValue ? modelValue.length : 0 }}
        <span v-if="maxCharacters > 0">/ {{ maxCharacters }}</span>
        characters
        <span v-if="minCharacters > 0">(min {{ minCharacters }})</span>
      </FormCaption>
      <FormCaption v-if="isCaptionShown">
        <slot name="caption" />
      </FormCaption>
    </div>
  </div>
</template>

<script setup>
import FormLabel from './FormLabel.vue'
import { getUniqueId } from '@/shared/utils/uniqueId.js'
import FormErrors from './FormErrors.vue'
import FormCaption from './FormCaption.vue'
import { computed, useAttrs, useSlots } from 'vue'

const emit = defineEmits(['update:modelValue', 'input'])

const props = defineProps({
  as: {
    type: String,
    default: 'input',
  },
  modelValue: {
    type: [String, Number],
    default: '',
  },
  isError: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: [Array, String],
    default: '',
  },
  inputClasses: {
    type: [Array, String, Object],
    default: () => [],
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  tooltip: {
    type: String,
    default: '',
  },
  minCharacters: {
    type: Number,
    default: 0,
  },
  maxCharacters: {
    type: Number,
    default: 0,
  },
})

const attrs = useAttrs()
const slots = useSlots()

const id = `growthmentor-input-${getUniqueId()}`

const errorClasses = computed(() => {
  return (
    props.isError &&
    'tw-bg-white tw-border-error-400 tw-text-error-500 placeholder:tw-text-error-500 focus:tw-ring-0 focus:tw-border-error-400 disabled:tw-text-error-500'
  )
})
const hasErrors = computed(() => {
  if (!props.isError) return false
  const isString = typeof props.errors === 'string'
  return isString ? props.errors : props.errors.length
})
const isCharacterCounterShown = computed(() => {
  return props.minCharacters > 0 || props.maxCharacters > 0
})
const isCaptionShown = computed(() => {
  return slots.caption
})
const sharedAttributes = computed(() => {
  return {
    ...attrs,
    class: ['tw-relative tw-shadow-sm', errorClasses.value, props.inputClasses],
    'aria-invalid': props.isError,
    'aria-describedby': id,
    'aria-readonly': Object.keys(attrs).includes('readonly').toString(),
    'aria-disabled': Object.keys(attrs).includes('disabled').toString(),
    'aria-required': props.isRequired && props.isRequired.toString(),
    id,
    value: props.modelValue,
    test_id: attrs.test_id,
  }
})

const handleInputChange = (event) => {
  const { min, max, value, type } = event.target
  if (value && type === 'number') {
    if (min && value < min) {
      event.target.value = min
    } else if (max && value > max) {
      event.target.value = max
    }
  }
  emit('update:modelValue', event.target.value)
  // 'input' emit is backward compatibility with Vue 2 v-model
  emit('input', event.target.value)
}
</script>

<script>
export default {
  name: 'FormInput',
  inheritAttrs: false,
}
</script>
